import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Phone, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallbackModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    callbackTime: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Bitte geben Sie Ihren Namen ein.');
      return;
    }
    if (!formData.phone) {
      toast.error('Bitte geben Sie Ihre Telefonnummer ein.');
      return;
    }
    if (!formData.callbackTime) {
      toast.error('Bitte wählen Sie eine Rückrufzeit aus.');
      return;
    }
    if (!formData.consent) {
      toast.error('Bitte stimmen Sie der Datenverarbeitung zu.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/api/callback`, {
        name: formData.name,
        phone: formData.phone,
        callback_time: formData.callbackTime
      });

      setIsSuccess(true);
      toast.success('Rückrufanfrage erfolgreich gesendet!');
      
      // Reset after 3 seconds and close
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', phone: '', callbackTime: '', consent: false });
        onClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting callback request:', error);
      toast.error('Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full lg:w-auto lg:min-w-[450px] lg:max-w-md bg-white rounded-t-3xl lg:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors z-10"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        {isSuccess ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              Vielen Dank!
            </h3>
            <p className="text-slate-600">
              Wir rufen Sie in Kürze zurück.
            </p>
          </div>
        ) : (
          /* Form State */
          <div className="p-6 lg:p-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900">Rückruf anfordern</h3>
                <p className="text-sm text-slate-500">Wir melden uns bei Ihnen</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <Label htmlFor="callback-name" className="text-slate-700">Name *</Label>
                <Input
                  id="callback-name"
                  placeholder="Ihr vollständiger Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="callback-phone" className="text-slate-700">Telefonnummer *</Label>
                <Input
                  id="callback-phone"
                  type="tel"
                  placeholder="+49 123 456789"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>

              {/* Callback Time */}
              <div>
                <Label htmlFor="callback-time" className="text-slate-700">Gewünschte Rückrufzeit *</Label>
                <Select 
                  value={formData.callbackTime} 
                  onValueChange={(value) => setFormData({ ...formData, callbackTime: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Bitte wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sofort">Sofort</SelectItem>
                    <SelectItem value="30min">In 30 Minuten</SelectItem>
                    <SelectItem value="1hour">In 1 Stunde</SelectItem>
                    <SelectItem value="afternoon">Heute Nachmittag</SelectItem>
                    <SelectItem value="tomorrow">Morgen Vormittag</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Privacy Notice */}
              <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg">
                Ihre Daten werden vertraulich behandelt und ausschließlich zur Bearbeitung Ihrer Rückrufanfrage verwendet.
              </p>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="callback-consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => setFormData({ ...formData, consent: checked })}
                  className="mt-0.5"
                />
                <Label htmlFor="callback-consent" className="text-sm text-slate-600 cursor-pointer">
                  Ich stimme der Verarbeitung meiner Daten zu. *
                </Label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg py-6"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Wird gesendet...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Rückruf anfordern
                  </span>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbackModal;
